
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { State, Getter } from "vuex-class"
import { CaseColumn } from "@/types/caseColumns"

import vSelect from "vue-select"
import { DataType, InsightCalculation, InsightCalculationTotal } from "@/types/insights"
import { cloneDeep } from "lodash"

import ListSelect from "@/components/Controls/ListSelect.vue"
import CalculationExpression from "../CalculationExpression.vue"
import Guid from "@/scripts/guid"
import { TenantFeatures } from "@/types/tenantFeatures"
import { InsightCalculationItem } from "@/types/insights/calculation"

enum CollationMode {
	All,
	Include,
	Exclude
}

@Component({
	components: {
		"v-select": vSelect,
		"list-select": ListSelect,
		"calculation-expression": CalculationExpression
	}
})
export default class CalculationTotal extends Vue {
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Prop({ default: null })
	value!: InsightCalculationItem<InsightCalculationTotal>

	@Prop({ default: () => [] })
	fields!: CaseColumn[]

	@Prop({ default: () => [] })
	insightCalculations!: InsightCalculation[]

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	controlId: string = ""
	collationMode = CollationMode

	calculationTotalsColumns: {key: string, label: string}[] = [
		{
			key: "title",
			label: "Title"
		},
		{
			key: "groupBy",
			label: "Group By"
		},
		{
			key: "calculationList",
			label: "Calculations"
		},
		{
			key: "subtractCalculationList",
			label: "Subtract Calculations"
		},
		{
			key: "expression",
			label: "Expression"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	get groupByCalculationFields (): any[] {
		if (!this.fields) {
			return []
		}

		const groupableDataTypes = [DataType.boolean, DataType.number, DataType.dateTime, DataType.array, DataType.arrayMultiple, DataType.userRole, DataType.auditUser]
		return this.fields.filter(f => groupableDataTypes.includes(f.fieldDataType!))
	}

	get allowCollation () {
		if (!this.value || !this.value.item || !this.value.item.groupBy) return false

		const field = this.fields.find(f => f.key === this.value.item.groupBy)
		if (!field) return false

		return field.dataType === "list" || field.dataType === "list-multiple" || field.dataType === "list-data"
	}

	get hasExpression (): boolean {
		return this.value && this.value.item && this.value.item.expression != null
	}

	set hasExpression (value: boolean) {
		if (value) {
			if (this.value.item.expression == null) {
				Vue.set(this.value.item, "expression", "{value}")
			}
		} else {
			Vue.set(this.value.item, "expression", null)
		}
		this.$emit("input", this.value)
	}

	get hasExtendedGrouping (): boolean {
		return this.extendedGroupingOptions.length > 0
	}

	get extendedGroupingOptions (): { value: string, label: string }[] {
		if (!this.value || !this.value.item.groupBy) return []

		const groupBy = this.fields.find(f => f.key === this.value.item.groupBy)
		if (!groupBy) return []

		if (groupBy.dataType === "user-role" &&
			this.tenantEnabledFeatures &&
			this.tenantEnabledFeatures[TenantFeatures.UserRoleLocation]) {
			return [
				{
					value: "user",
					label: "User"
				},
				{
					value: "location",
					label: "Location"
				}
			]
		}

		return []
	}

	private mounted () {
		this.controlId = Guid.newGuid()

		// to be used after disabling tenant feature for expression-based calculation totals
		this.clearAnyUnsupportedExpression()
	}

	private clearAnyUnsupportedExpression () {
		if (this.value.item.expression && !this.value.item.expression.includes("{value}")) {
			Vue.set(this.value.item, "expression", "{value}")
		}
	}

	private insightsCalculationTotalList (groupBy: string): InsightCalculation[] {
		if (!groupBy && (!this.value || !this.value.item.calculationList || this.value.item.calculationList.length === 0)) {
			return this.insightCalculations
		}

		return groupBy
			? this.insightCalculations.filter(c => c.groupBy === groupBy)
			: this.insightCalculations.filter(c => !c.groupBy)
	}

	showCalculationFormattingRules (calculation: InsightCalculation | null) {
		this.$emit("showCalculationFormattingRules", calculation)
	}

	private setCollationMode (calculationTotal: InsightCalculationTotal, collateMode: CollationMode) {
		switch (collateMode) {
			case CollationMode.All:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Include:
				Vue.set(calculationTotal, "collateInclude", [])
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Exclude:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", [])
				break
		}
	}

	private getCollationMode (calculationTotal: InsightCalculationTotal): CollationMode {
		return calculationTotal.collateInclude
			? CollationMode.Include
			: calculationTotal.collateExclude
				? CollationMode.Exclude
				: CollationMode.All
	}

	private groupByField (groupBy: string) {
		return this.fields.find(f => f.key === groupBy)
	}

	private calculationAdded (calculations: string[]) {
		if (this.value && this.value.item && calculations.length > 0) {
			const groupBy = calculations
				.map(c => this.insightCalculations.findAndGet(calculation => calculation.key === c, c => c.groupBy, null))
				.distinct()

			this.value.item.groupBy = groupBy.length > 0 ? groupBy[0] : ""
		}
	}

	private expressionCheckChanged (calculation: InsightCalculation, hasExpression: boolean) {
		if (hasExpression) {
			if (!calculation.expression) {
				calculation.expression = "{value}"
			}
		} else {
			calculation.expression = null
		}
	}
	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}

	async groupByChanged (newValue: string) {
		if (this.tenantEnabledFeatures && this.tenantEnabledFeatures[TenantFeatures.InsightFilterWithCalculations]) {
			if (newValue !== null) {
				this.$emit("filter-calculation-grouped", this.value, newValue)
			} else {
				this.value.item.groupBy = newValue
			}
		} else {
			this.value.item.groupBy = newValue
		}
	}
}
